import { DataSource, DataSourceFilters } from "./DataSource";
import {
  IGenericRequestParametersTranslator,
  IPaginationParametersTranslator,
  PropertyTranslator,
} from "./GenericTranslator";
import {
  GenericEntityOrderBy,
  EntityConstants,
  IGenericEntity,
  IGenericRequestParameters,
  IPaginationParameters,
  IPermissionedEntity,
} from "./GenericTypes";

// IconNMR
export const iconNMRConstants: EntityConstants<DataSource, DataSourceFilters> = {
  resource: "data_sources",
  frontendIndexRoute: "iconnmr",
  entitySingular: "IconNMR monitoring",
  entityPlural: "IconNMR monitoring",
  icon: "radar",
};

// IconNMR History
export interface IconNMRHistoryEntry extends IGenericEntity<number>, IPermissionedEntity {
  startTime?: string;
  completionTime?: string;
  user: string;
  holder: string;
  diskUnit: string;
  datasetName: string;
  experimentNo?: number;
  experimentName: string;
  statusInformationForATM: IconNMRProgressState | null;
  rotation: IconNMRProgressState | null;
  lock: IconNMRProgressState | null;
  shim: IconNMRProgressState | null;
  acquisition: IconNMRProgressState | null;
  processing: IconNMRProgressState | null;
  originatorTitle: string;
  remarks: string;
  sampleLoad: IconNMRProgressState | null;
  tubeId: string;
  analysisResult: IconNMRProgressState | null;
  analysisOutput: string;
  sBase: string;
  setupRow: number;
  consistency: IconNMRProgressState | null;
  dataSource: DataSource;
}

type IconNMRProgressState = "S" | "Y" | "X";

export interface IconNMRHistoryEntryFilters
  extends IGenericRequestParameters<IconNMRHistoryEntry, IconNMRHistoryEntrySortingOptions>, IPaginationParameters {
  dataSourceIds?: number[] | null;
}

export const IconNMRHistoryEntrySortingOptionsConsts = [
  ...GenericEntityOrderBy,
  "START_TIME",
  "COMPLETION_TIME",
  "USER",
  "HOLDER",
  "DISK_UNIT",
  "EXPNO",
  "DATA_SOURCE",
] as const;

export type IconNMRHistoryEntrySortingOptions = (typeof IconNMRHistoryEntrySortingOptionsConsts)[number];

export const IconNMRHistoryEntryFiltersTranslator: PropertyTranslator<IconNMRHistoryEntryFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,

  dataSourceIds: "Data-source-IDs",
};

export const iconNMRHistoryConstants: EntityConstants<IconNMRHistoryEntry, IconNMRHistoryEntryFilters> = {
  resource: "iconnmr_history_entries",
  frontendIndexRoute: "iconnmr",
  entitySingular: "iconNMR history entry",
  entityPlural: "iconNMR history entries",
  icon: "radar",
};

export const IconNMRHistoryFieldLabels: PropertyTranslator<IconNMRHistoryEntry> & {
  duration: string;
} = {
  acquisition: "Acquisition",
  analysisOutput: "Analysis output",
  analysisResult: "Analysis result",
  completionTime: "Completion time",
  consistency: "Consistency",
  datasetName: "Dataset name",
  dataSource: "Data source",
  diskUnit: "Disk unit",
  experimentName: "Experiment name",
  experimentNo: "Experiment no",
  holder: "Holder",
  id: "History entry ID",
  lock: "Lock",
  originatorTitle: "Originator title",
  processing: "Processing",
  permissions: "Permissions",
  remarks: "Remarks",
  rotation: "Rotation",
  sampleLoad: "Sample load",
  setupRow: "Setup row",
  shim: "Shim",
  sBase: "S base",
  startTime: "Start time",
  statusInformationForATM: "Status information for ATM",
  tubeId: "Tube ID",
  user: "User",
  duration: "Duration",
} as const;

// Holders
export interface IconNMRHolder extends IGenericEntity<number>, IPermissionedEntity {
  holder: string;
  status: IconNMRHolderStatus;
  dataset: string;
  expno: number;
  solvent: string[];
  experiment: string[];
  path: string;
  duration?: number;
  user: string;
  queuePosition: number;
  dataSource: DataSource;
  modifiedOn: string;
}

export const IconNMRHolderFieldLabels: PropertyTranslator<IconNMRHolder> = {
  dataset: "Dataset",
  dataSource: "Data source",
  duration: "Duration in seconds",
  experiment: "Experiment",
  expno: "Expno",
  holder: "Holder",
  id: "Holder ID",
  modifiedOn: "Modified on",
  path: "Path",
  permissions: "Permissions",
  queuePosition: "Queue position",
  solvent: "Solvent",
  status: "Status",
  user: "User",
} as const;

export type IconNMRHolderStatus = "Unknown" | "Submitted" | "Running" | "Completed" | "Available";

export interface IconNMRHolderFilters
  extends IGenericRequestParameters<IconNMRHolder, IconNMRHolderSortingOptions>, IPaginationParameters {
  dataSourceIds?: number[] | null;
  statuses?: IconNMRHolderStatus[] | null;
}

export const iconNMRHoldersConstants: EntityConstants<IconNMRHolder, IconNMRHolderFilters> = {
  resource: "iconnmr_holders",
  frontendIndexRoute: "iconnmr",
  entitySingular: "iconNMR queue",
  entityPlural: "iconNMR queue",
  icon: "radar",
};
export const IconNMRHolderSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  "HOLDER",
  "STATUS",
  "EXPNO",
  "DURATION",
  "USER",
  "MODIFIED_ON",
  "QUEUE_POSITION",
  "DATA_SOURCE",
] as const;

export type IconNMRHolderSortingOptions = (typeof IconNMRHolderSortingOptionsConsts)[number];

export const IconNMRHolderFiltersTranslator: PropertyTranslator<IconNMRHolderFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,

  dataSourceIds: "Data-source-IDs",
  statuses: "Statuses",
};

// Status
export interface IconNMRStatus {
  dataSource: DataSource;
  queuedExperiments: number;
  totalQueueTimeInSeconds: number;
  acquisitionRunning: boolean;
}

export const iconNMRStatusConstants: EntityConstants<IconNMRStatus> = {
  resource: "iconnmr_status",
  frontendIndexRoute: "iconnmr",
  entitySingular: "iconNMR status",
  entityPlural: "iconNMR status",
  icon: "cctv",
};

export const IconNMRStatusFieldLabels: PropertyTranslator<IconNMRStatus> = {
  dataSource: "Data source",
  queuedExperiments: "Queued experiments",
  totalQueueTimeInSeconds: "Total queue time in seconds",
  acquisitionRunning: "Acquisition",
} as const;
